import { ModuleKey } from './types';

// Display metadata for the module rail, upsell card, and Tenant Provisioning checkboxes.
// The English `label`/`blurb` stay for callers outside the dashboard; translated UI uses
// `labelKey`/`blurbKey` (keys in the `dashboard` namespace).
// Keyed by every ModuleKey (including `holidays`, which is never gated) so callers never hit an
// undefined lookup.
export interface ModuleMeta {
    label: string;
    blurb: string;
    labelKey: `modules.${ModuleKey}.label`;
    blurbKey: `modules.${ModuleKey}.blurb`;
}

const meta = (key: ModuleKey, label: string, blurb: string): ModuleMeta =>
    ({ label, blurb, labelKey: `modules.${key}.label`, blurbKey: `modules.${key}.blurb` });

export const MODULE_META: Record<ModuleKey, ModuleMeta> = {
    attendance: meta('attendance', 'Attendance', 'Clock-ins, lateness, overtime and daily presence.'),
    leave: meta('leave', 'Leave', 'Leave applications, approvals and balances.'),
    people: meta('people', 'People', 'Headcount, departments and roster composition.'),
    holidays: meta('holidays', 'Holidays', 'Institution holiday calendar.'),
    payroll: meta('payroll', 'Payroll', 'Payroll runs, totals and headcount on last run.'),
    accounting: meta('accounting', 'Accounting', 'Books, payroll ledger, cash & bank, P&L and balance sheet (paid add-on).'),
};

// Canonical gated module set (mirrors backend com.kervolt.attendance.common.DashboardModule.keys()).
// `holidays` is intentionally excluded — it is not a dashboard module.
export const GATED_MODULE_KEYS: ModuleKey[] = [
    'attendance', 'leave', 'people', 'payroll', 'accounting',
];

/**
 * Whether a module participates in institution module-gating at all.
 *
 * `enabledModules` only ever holds gated keys, so callers must not treat "absent from
 * enabledModules" as "disabled" — an ungated module (`holidays`) is absent by definition and
 * must stay visible. Boards filter with this rather than a bare `enabledModules.has()`.
 */
export function isGatedModule(module: ModuleKey): boolean {
    return GATED_MODULE_KEYS.includes(module);
}
